// SEQ-180 legibility follow-up (15 September 2026): one tooltip for the four
// project-grain phase 1 charts.
//
// WHAT WAS WRONG. The bar chart passed Recharts a `labelFormatter` fed the
// AXIS's truncated name, so the tooltip said "Project: Esperance Hope Isla…":
// the one place the full name had to survive was repeating the axis's
// abbreviation. The scatters went the other way and listed fourteen rows in a
// fixed order, so the two figures actually being plotted were somewhere in the
// middle of a wall of numbers with nothing to say which axis was which.
//
// WHAT IT SAYS NOW, in the same three-part shape the built widgets already use
// (PnlWidgetGenericCharts.tsx, widgetChartLabels.ts): the project's full name,
// then the grouping in words, then the figures. The chart's own two axes come
// first and are marked as the ones being plotted; everything else follows as
// context. A figure whose point was pinned to the edge of a clamped axis says
// so, with its real value, because the pin is a drawing decision and must never
// become a reported number.
import { Fragment } from "react";
import { motion, useReducedMotion } from "framer-motion";
import type { ProjectRollup } from "../../features/pnlAnalytics/rollup";
import { formatMetricValue } from "../../lib/pnl/formatMetricValue";
import type { PnlUnit } from "../../lib/pnl/pnlAnalytics";
import { groupByExplainer } from "../../features/pnlAnalytics/groupByVocabulary";
import { tooltipVariants } from "../../features/pnlAnalytics/pnlMotion";
import "./PnlWidgetCharts.css";

/** The numeric fields of a project rollup, which is everything a row may name. */
export type ProjectMetricKey = {
  [K in keyof ProjectRollup]: ProjectRollup[K] extends number | null ? K : never;
}[keyof ProjectRollup];

export interface ProjectTooltipRow {
  /** The words the reader sees, e.g. "Overtime hours %". */
  name: string;
  key: ProjectMetricKey;
  unit: PnlUnit;
  /** An axis this chart is plotting. Shown first, with a swatch, and set in the value weight. */
  axis?: boolean;
  /** The mark colour for an axis row's swatch. */
  colour?: string;
  /**
   * The field on the POINT that says whether this project ran off this axis.
   *
   * Per point, not per chart. The first version carried a boolean taken from
   * the chart's own `clamped` flag, which told all twenty-two points they were
   * pinned on a chart where three of them were.
   */
  pinnedBy?: "pinnedX" | "pinnedY";
}

/** The datum a chart hands its tooltip: a project, plus whatever the chart added to it. */
type TooltipDatum = ProjectRollup & { projectName?: string; pinnedX?: boolean; pinnedY?: boolean };

interface PnlProjectTooltipProps {
  rows: ProjectTooltipRow[];
  /** Injected by Recharts through `content`. */
  active?: boolean;
  /** Injected by Recharts through `content`. */
  payload?: { payload?: TooltipDatum }[];
}

/** What a pinned row adds under the figures. Never replaces the figure itself. */
export const PINNED_TOOLTIP_NOTE = "This point sits past the end of the axis and is drawn on its edge. The figures above are the real ones.";

export function PnlProjectTooltip({ rows, active, payload }: PnlProjectTooltipProps) {
  const reduce = useReducedMotion() ?? false;
  if (!active || !payload || payload.length === 0) return null;

  const project = payload[0]?.payload;
  if (!project) return null;

  const title = (project.projectName ?? "").trim();
  const ordered = [...rows.filter((row) => row.axis), ...rows.filter((row) => !row.axis)];
  const isPinned = (row: ProjectTooltipRow) => row.pinnedBy !== undefined && project[row.pinnedBy] === true;
  const anyPinned = rows.some(isPinned);

  return (
    <motion.div
      key={project.projectId}
      className="pnl-tooltip"
      role="tooltip"
      variants={tooltipVariants(reduce)}
      initial="hidden"
      animate="visible"
    >
      <div className="pnl-tooltip-title">{title.length > 0 ? title : "Unnamed project"}</div>
      <div className="pnl-tooltip-grouping">{groupByExplainer("project").tooltipLine}</div>
      <div className="pnl-tooltip-rows">
        {ordered.map((row) => (
          <Fragment key={row.key}>
            <span
              className="pnl-tooltip-swatch"
              style={row.axis ? { background: row.colour ?? "var(--color-primary)" } : { background: "transparent" }}
              aria-hidden="true"
            />
            <span className="pnl-tooltip-name" data-axis={row.axis ? "true" : undefined}>
              {row.name}
            </span>
            <span className="pnl-tooltip-value" data-pinned={isPinned(row) ? "true" : undefined} data-seq-private>
              {formatMetricValue(project[row.key], row.unit)}
            </span>
          </Fragment>
        ))}
      </div>
      {anyPinned && <p className="pnl-tooltip-note">{PINNED_TOOLTIP_NOTE}</p>}
    </motion.div>
  );
}
